import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import guardiansService from "../services/guardiansService";
import "../assets/guardiansAfterDeadline.css";
import Loader from "../components/Loader";

const GuardiansAfterDeadline = () => {
  const [guardians, setGuardians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Permission checking function
  const hasPermission = (permission) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission);
  };

  useEffect(() => {
    loadGuardiansAfterDeadline();
  }, []);

  const loadGuardiansAfterDeadline = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("Loading guardians after deadline...");
      const data = await guardiansService.getGuardiansAfterDeadline();
      console.log("Guardians data received:", data);
      console.log("Data length:", data.length);
      console.log("Data type:", typeof data);
      if (data.length > 0) {
        console.log("First guardian status:", data[0].status);
        console.log("First guardian status_id:", data[0].status_id);
        console.log("First guardian user:", data[0].user);
      }
      setGuardians(data);
      console.log("Guardians state set to:", data);

      if (data.length === 0) {
        toast.info("No guardians found after deadline");
      }
    } catch (err) {
      console.error("Error loading guardians after deadline:", err);
      setError("Failed to load guardians after deadline");
      toast.error("Failed to load guardians after deadline");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not provided";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB");
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    if (!status) return null;
    return (
      <span
        className="status-badge"
        style={{
          backgroundColor: status.color || "#007bff",
          color: "white",
          padding: "4px 8px",
          borderRadius: "4px",
          fontSize: "12px",
        }}
      >
        {status.name}
      </span>
    );
  };

  const calculateDaysOverdue = (dateToSend) => {
    if (!dateToSend) return 0;
    try {
      const deadline = new Date(dateToSend);
      const now = new Date();
      const diffTime = now - deadline;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    } catch {
      return 0;
    }
  };

  // Sorting function
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Sort icon component
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <span style={{ marginLeft: '5px', opacity: 0.4, fontSize: '12px' }}>
          ⇅
        </span>
      );
    }
    return (
      <span style={{ marginLeft: '5px', fontSize: '12px' }}>
        {sortDirection === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  const handleAction = async (action, guardian) => {
    switch (action) {
      case "show":
        navigate(`/guardians/${guardian.id}`);
        break;
      case "edit":
        navigate(`/guardians/${guardian.id}/edit`);
        break;
      case "assign":
        navigate(`/rooms/assign/${guardian.id}`);
        break;
      case "set-payment":
        if (guardian.latest_rent?.room?.id) {
          navigate(
            `/property-application/payment/create/${guardian.latest_rent.room.id}`
          );
        }
        break;
      case "decline-doc":
        navigate(`/property-application/documentation/decline/${guardian.id}`);
        break;
      case "dismiss":
        if (guardian.latest_rent?.id) {
          if (window.confirm("Are you sure you want to dismiss guardian from property?")) {
            navigate(`/property-application/rent/dismiss/${guardian.latest_rent.id}`);
          }
        }
        break;
      case "not-interested":
        if (window.confirm("Are you sure you want to mark Guardian as Not Interested?")) {
          navigate(`/property-application/guardian/dismiss/${guardian.id}`);
        }
        break;
      case "mark-as-paid":
        if (guardian.latest_rent?.id) {
          navigate(`/payments/mark-as-paid-initial/${guardian.latest_rent.id}`);
        }
        break;
      case "sign-agreement":
        if (guardian.latest_rent?.id) {
          navigate(`/property-application/sign-agreement/${guardian.latest_rent.id}`);
        }
        break;
      case "start-rent":
        if (guardian.latest_rent?.id) {
          navigate(`/rents/${guardian.latest_rent.id}/start`);
        }
        break;
      case "change-rent":
        if (guardian.latest_rent?.room?.id) {
          // Laravel route: /rents/{room}/payments/edit
          navigate(`/rents/${guardian.latest_rent.room.id}/payments/edit`);
        }
        break;
      case "blacklist":
        if (
          window.confirm(
            `Are you sure you want to blacklist ${guardian.user?.first_name} ${guardian.user?.last_name}?`
          )
        ) {
          try {
            await guardiansService.blacklistGuardian(guardian.id);
            toast.success("Guardian successfully updated");
            loadGuardiansAfterDeadline(); // Refresh the list
          } catch (error) {
            console.error("Error blacklisting guardian:", error);
            toast.error("Failed to blacklist guardian");
          }
        }
        break;
      case "unblacklist":
        if (
          window.confirm(
            `Are you sure you want to unblacklist ${guardian.user?.first_name} ${guardian.user?.last_name}?`
          )
        ) {
          try {
            await guardiansService.unblacklistGuardian(guardian.id);
            toast.success("Guardian successfully updated");
            loadGuardiansAfterDeadline(); // Refresh the list
          } catch (error) {
            console.error("Error unblacklisting guardian:", error);
            toast.error("Failed to unblacklist guardian");
          }
        }
        break;
      case "set-as-registered":
        if (
          window.confirm(
            `Are you sure you want to change ${guardian.user?.first_name} ${guardian.user?.last_name}'s status to Documents Required?`
          )
        ) {
          try {
            await guardiansService.setAsRegistered(guardian.id);
            toast.success("Guardian status changed to Documents Required successfully");
            loadGuardiansAfterDeadline(); // Refresh the list
          } catch (error) {
            console.error("Error setting guardian as registered:", error);
            toast.error("Failed to change guardian status");
          }
        }
        break;
      case "remove":
        // Handle remove action with confirmation
        if (window.confirm("Are you sure you want to remove this guardian?")) {
          toast.info(
            `Removing guardian ${guardian.user?.first_name} ${guardian.user?.last_name}`
          );
        }
        break;
      default:
        break;
    }
  };

  // Filter and sort guardians
  console.log("Current guardians state:", guardians);
  console.log("Current search term:", searchTerm);
  const filteredAndSortedGuardians = guardians
    .filter((guardian) => {
      const searchLower = searchTerm.toLowerCase();
      const fullName = `${guardian.user?.first_name || ""} ${
        guardian.user?.last_name || ""
      }`.toLowerCase();
      const ugd = (guardian.unique_guardian_number || "").toLowerCase();
      const status = (guardian.status?.name || "").toLowerCase();

      return (
        fullName.includes(searchLower) ||
        ugd.includes(searchLower) ||
        status.includes(searchLower)
      );
    })
    .sort((a, b) => {
      if (!sortField) return 0;

      let aValue, bValue;

      switch (sortField) {
        case "ugd":
          aValue = a.unique_guardian_number || "";
          bValue = b.unique_guardian_number || "";
          break;
        case "firstName":
          aValue = a.user?.first_name || "";
          bValue = b.user?.first_name || "";
          break;
        case "lastName":
          aValue = a.user?.last_name || "";
          bValue = b.user?.last_name || "";
          break;
        case "dateToSend":
          aValue = a.rents?.[0]?.date_to_send_documentation || "";
          bValue = b.rents?.[0]?.date_to_send_documentation || "";
          // Convert to Date for proper comparison
          if (aValue) aValue = new Date(aValue);
          if (bValue) bValue = new Date(bValue);
          break;
        case "status":
          aValue = a.status?.name || "";
          bValue = b.status?.name || "";
          break;
        case "daysOverdue":
          aValue = calculateDaysOverdue(a.rents?.[0]?.date_to_send_documentation);
          bValue = calculateDaysOverdue(b.rents?.[0]?.date_to_send_documentation);
          break;
        default:
          return 0;
      }

      // Handle null/undefined values
      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;

      // Compare values
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  console.log("Filtered and sorted guardians:", filteredAndSortedGuardians);
  console.log("Filtered guardians length:", filteredAndSortedGuardians.length);

  // Pagination logic
  const totalPages = Math.ceil(filteredAndSortedGuardians.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const paginatedGuardians = filteredAndSortedGuardians.slice(startIndex, endIndex);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="container-fluid" style={{ backgroundColor: "#FFFFFF" }}>
      {/* Breadcrumb */}
      {/* <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black" }}
        >
          Dashboard
        </span>
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black" }}
        >
          / Guardians
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">After deadline</span>
        </div>
      </div> */}

      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black" }}
        >
          <a href="/dashboard">Dashboard</a>
        </span>
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black" }}
        >
          <a href="/guardians">/ Guardians</a>
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">After deadline</span>
        </div>
      </div>

      {/* Page Title */}

      {/* Main Content */}

      <div className="row">
        <div className="col-md-12">
          <div
            className="panel-group"
            style={{ background: "#F8F9FA", border: "none" }}
          >
            {error ? (
              <div className="alert alert-warning">
                <strong>Note:</strong> {error}
                <br />
                <small>
                  This could be because the backend server is not running or
                  there are no guardians matching the criteria.
                </small>
              </div>
            ) : filteredAndSortedGuardians.length > 0 ? (
              <div
                className="dataTable_wrapper"
                style={{ background: "#F8F9FA", border: "none" }}
              >
                {" "}
                <h3>Guardian after deadline</h3>
                {/* Table Controls */}
                <div className="row mb-3" style={{ padding: "10px" }}>
                  <div className="col-md-6">
                    <label
                      htmlFor="entriesPerPage"
                      className="form-label"
                      style={{ display: "flex", flexDirection: "row", gap: 8 }}
                    >
                      Show
                      <select
                        id="entriesPerPage"
                        // className="form-select form-select-sm d-inline-block w-auto mx-2"
                        value={entriesPerPage}
                        onChange={(e) => {
                          setEntriesPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      entries
                    </label>
                  </div>
                  <div className="col-md-6 text-end">
                    <input
                      type="text"
                      className="form-control form-control-sm d-inline-block w-auto"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                {/* Data Table */}
                <div className="table-responsive">
                  <table className="table table-hover dt-responsive">
                    <thead>
                      <tr>
                        <th
                          className="js-tooltip sortable"
                          data-toggle="tooltip"
                          data-placement="top"
                          title="Unique Guardian Number - Click to sort"
                          data-container="body"
                          style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                          onClick={() => handleSort("ugd")}
                        >
                          UGD{getSortIcon("ugd")}
                        </th>
                        <th
                          className="sortable"
                          style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                          onClick={() => handleSort("firstName")}
                          title="Click to sort by first name"
                        >
                          First name{getSortIcon("firstName")}
                        </th>
                        <th
                          className="sortable"
                          style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                          onClick={() => handleSort("lastName")}
                          title="Click to sort by last name"
                        >
                          Last name{getSortIcon("lastName")}
                        </th>
                        <th
                          className="sortable"
                          style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                          onClick={() => handleSort("dateToSend")}
                          title="Click to sort by documentation deadline"
                        >
                          Date to send documentation{getSortIcon("dateToSend")}
                        </th>
                        <th
                          className="sortable"
                          style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                          onClick={() => handleSort("status")}
                          title="Click to sort by status"
                        >
                          Status{getSortIcon("status")}
                        </th>
                        <th className="action-head">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedGuardians.map((guardian) => (
                        <tr key={guardian.id}>
                          <td>{guardian.unique_guardian_number}</td>
                          <td>{guardian.user?.first_name || "-"}</td>
                          <td>{guardian.user?.last_name || "-"}</td>
                          <td>
                            {formatDate(
                              guardian.rents?.[0]?.date_to_send_documentation
                            )}
                            {guardian.rents?.[0]
                              ?.date_to_send_documentation && (
                              <div className="text-muted small">
                                {calculateDaysOverdue(
                                  guardian.rents[0].date_to_send_documentation
                                )}{" "}
                                days overdue
                              </div>
                            )}
                          </td>
                          <td>{getStatusBadge(guardian.status)}</td>
                          <td>
                            <div
                              className="action-buttons"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                flexDirection: "row",
                                width: "350px",
                              }}
                            >
                              {/* Show button - Permission check: guardians.show */}
                              {hasPermission('guardians.show') && (
                                <button
                                  className="btn btn-sm btn-warning btn-action"
                                  onClick={() => handleAction("show", guardian)}
                                  title="Show Guardian"
                                >
                                  Show
                                </button>
                              )}

                              {/* Edit button - Permission check: guardians.edit */}
                              {hasPermission('guardians.edit') && (
                                <button
                                  className="btn btn-sm btn-primary btn-action"
                                  onClick={() => handleAction("edit", guardian)}
                                  title="Edit Guardian"
                                >
                                  Edit
                                </button>
                              )}

                              {/* Assign button - Laravel logic: Registered (1), Documents Required (2), Previous Guardian (7) + permission check */}
                              {[1, 2, 7].includes(guardian.status_id) && hasPermission('guardians.assign') && (
                                <button
                                  className="btn btn-sm btn-info btn-action"
                                  onClick={() => handleAction("assign", guardian)}
                                  title="Assign Guardian"
                                >
                                  Assign
                                </button>
                              )}

                              {/* Set Payment button - Property Assigned (3) + multiple permission checks + rent exists */}
                              {guardian.status_id === 3 && hasPermission('property-application.payment.set') && hasPermission('payments.list') && guardian.rents?.[0]?.room && (
                                <button
                                  className="btn btn-sm btn-warning btn-action"
                                  onClick={() => handleAction("set-payment", guardian)}
                                  title="Set Payment"
                                >
                                  Set payment
                                </button>
                              )}

                              {/* Decline Doc button - Property Assigned (3) + permission check */}
                              {guardian.status_id === 3 && hasPermission('property-application.documentation.decline') && (
                                <button
                                  className="btn btn-sm btn-danger btn-action"
                                  onClick={() => handleAction("decline-doc", guardian)}
                                  title="Decline Documentation"
                                >
                                  Decline doc
                                </button>
                              )}

                              {/* Set As Registered button - Declined (9) + permission check */}
                              {guardian.status_id === 9 && hasPermission('guardians.canRestoreDeclined') && (
                                <button
                                  className="btn btn-sm btn-danger btn-action"
                                  onClick={() => handleAction("set-as-registered", guardian)}
                                  title="Set As Registered (Documents Required)"
                                >
                                  Set As Registered
                                </button>
                              )}

                              {/* Dismiss/Not Interested button - Documents Required (2) + permission check + rent logic */}
                              {guardian.status_id === 2 && hasPermission('property-application.rent.dismiss') && (
                                guardian.rents?.[0] ? (
                                  <button
                                    className="btn btn-sm btn-danger btn-action"
                                    onClick={() => handleAction("dismiss", guardian)}
                                    title="Dismiss from property"
                                  >
                                    Dismiss
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-sm btn-danger btn-action"
                                    onClick={() => handleAction("not-interested", guardian)}
                                    title="Mark as not interested"
                                  >
                                    Not Interested
                                  </button>
                                )
                              )}

                              {/* Mark as Paid button - Payment Pending (4) + permission check + rent + initial payments exist */}
                              {hasPermission('property-application.mark-as-paid') && guardian.status_id === 4 && guardian.rents?.[0] && guardian.rents[0].initialPayments?.length > 0 && (
                                <button
                                  className="btn btn-sm btn-info btn-action"
                                  onClick={() => handleAction("mark-as-paid", guardian)}
                                  title="Mark as Paid"
                                >
                                  Mark as paid
                                </button>
                              )}

                              {/* Sign Licence Agreement button - Awaiting Key Collection (5) + permission check + rent + no signed agreement */}
                              {guardian.status_id === 5 && hasPermission('rents.start') && guardian.rents?.[0] && !guardian.rents[0].signed_license_agreement && (
                                <button
                                  className="btn btn-sm btn-info btn-action"
                                  onClick={() => handleAction("sign-agreement", guardian)}
                                  title="Sign Licence Agreement"
                                >
                                  Sign Licence Agreement
                                </button>
                              )}

                              {/* Start Rent button - Awaiting Key Collection (5) + permission check + rent + signed agreement */}
                              {guardian.status_id === 5 && hasPermission('rents.start') && guardian.rents?.[0] && guardian.rents[0].signed_license_agreement && (
                                <button
                                  className="btn btn-sm btn-success btn-action"
                                  onClick={() => handleAction("start-rent", guardian)}
                                  title="Start Rent"
                                >
                                  Start rent
                                </button>
                              )}

                              {/* Change Rent button - Permission check + canHaveChangedPaymentsOnRent + rent exists */}
                              {hasPermission('rents.payments.edit') && [4, 5, 6, 12].includes(guardian.status_id) && guardian.rents?.[0] && (
                                <button
                                  className="btn btn-sm btn-info btn-action"
                                  onClick={() => handleAction("change-rent", guardian)}
                                  title="Change Rent"
                                >
                                  Change rent
                                </button>
                              )}

                              {/* Blacklist button - Permission check + not Blacklisted (8) */}
                              {hasPermission('guardians.destroy') && guardian.status_id !== 8 && (
                                <button
                                  className="btn btn-sm btn-danger btn-action"
                                  onClick={() => handleAction("blacklist", guardian)}
                                  title="Add to Blacklist"
                                >
                                  Blacklist
                                </button>
                              )}

                              {/* Unblacklist button - Permission check + Blacklisted (8) */}
                              {hasPermission('guardians.destroy') && guardian.status_id === 8 && (
                                <button
                                  className="btn btn-sm btn-warning btn-action"
                                  onClick={() => handleAction("unblacklist", guardian)}
                                  title="Remove from Blacklist"
                                >
                                  Unblacklist
                                </button>
                              )}

                              {/* Remove button - Permission check only */}
                              {hasPermission('guardians.destroy') && (
                                <button
                                  className="btn btn-sm btn-danger btn-action"
                                  onClick={() => handleAction("remove", guardian)}
                                  title="Remove Guardian"
                                >
                                  Remove
                                </button>
                              )}

                              {/* Decline button - Registered (1) OR Previous Guardian (7) + permission check */}
                              {[1, 7].includes(guardian.status_id) && hasPermission('guardians.edit') && (
                                <button
                                  className="btn btn-sm btn-danger btn-action"
                                  onClick={() => handleAction("decline", guardian)}
                                  title="Decline Guardian"
                                >
                                  Decline
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                <div className="row">
                  <div className="col-md-6">
                    <p className="text-muted">
                      Showing {startIndex + 1} to{" "}
                      {Math.min(endIndex, filteredAndSortedGuardians.length)} of{" "}
                      {filteredAndSortedGuardians.length} entries
                    </p>
                  </div>
                  <div className="col-md-6">
                    <nav aria-label="Table pagination">
                      <ul className="pagination pagination-sm justify-content-end">
                        <li
                          className={`page-item ${
                            currentPage === 1 ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </button>
                        </li>
                        {Array.from(
                          { length: totalPages },
                          (_, i) => i + 1
                        ).map((page) => (
                          <li
                            key={page}
                            className={`page-item ${
                              currentPage === page ? "active" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </button>
                          </li>
                        ))}
                        <li
                          className={`page-item ${
                            currentPage === totalPages ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <div className="alert alert-info">
                  <h4>No Guardians After Deadline</h4>
                  <p>
                    There are currently no guardians whose documentation
                    deadline has passed.
                  </p>
                  <p>
                    <small>This could mean:</small>
                  </p>
                  <ul className="text-start">
                    <li>
                      All guardians have submitted their documentation on time
                    </li>
                    <li>No documentation deadlines have been set</li>
                    <li>The backend server is not running</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuardiansAfterDeadline;
